/**
 * Every editable field of the Employee Master, grouped into the tabs HR asked for.
 * `calc: true` columns are read-only and computed live by lib/salaryCalc.js.
 */
export const GROUPS = [
  { key: 'identity', label: 'Identity' },
  { key: 'job', label: 'Job & Pay Scale' },
  { key: 'salary', label: 'Salary (auto-calc)' },
  { key: 'statutory', label: 'Statutory & Flags' },
  { key: 'bank', label: 'Bank' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'settings', label: 'Attendance Settings' },
  { key: 'all', label: 'All Fields' },
];

export const GENDERS = ['male', 'female', 'other'];
export const SALARY_TYPES = ['breakdown', 'consolidated'];
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
export const APPROVAL_STATUS = ['Approved', 'Pending', 'Not Applicable'];

// Standard campus master lists — merged with whatever already exists in the data so HR can
// pick a known value OR type a brand-new one (combo fields).
export const DEFAULT_OPTIONS = {
  categories: [
    'Professor', 'Associate Professor', 'Assistant Professor', 'Lecturer', 'Visiting Faculty',
    'School Teacher', 'Coaching Faculty', 'Lab Instructor', 'Non-teaching Staff',
    'Contract/Adhoc Faculty', 'Guest Faculty',
  ],
  appointment_types: ['Permanent', 'Probation', 'Contract', 'Adhoc', 'Guest', 'Visiting', 'Part Time'],
  qualifications: ['PhD', 'M.Tech', 'M.E', 'M.Phil', 'M.Sc', 'MBA', 'M.Com', 'MA', 'M.Ed',
    'B.Tech', 'B.E', 'B.Sc', 'B.Com', 'BA', 'B.Ed', 'Diploma', 'ITI', 'HSC', 'SSC', 'Other'],
  banks: ['State Bank of India', 'Bank of Maharashtra', 'HDFC Bank', 'ICICI Bank', 'Axis Bank',
    'Bank of Baroda', 'Union Bank of India', 'Kotak Mahindra Bank', 'IDBI Bank'],
  pay_scales: ['Level 10 (57700-182400)', 'Level 11 (68900-205500)', 'Level 12 (79800-211500)',
    'Level 13A (131400-217100)', 'Level 14 (144200-218200)', 'Consolidated'],
};

export const mergeOptions = (fromServer = {}) => {
  const out = { ...fromServer };
  Object.entries(DEFAULT_OPTIONS).forEach(([k, list]) => {
    out[k] = [...new Set([...(fromServer[k] || []), ...list])].sort();
  });
  return out;
};

export const FIELDS = [
  // ---------- Identity ----------
  { key: 'first_name', label: 'First Name', type: 'text', group: 'identity', w: 'w-32' },
  { key: 'middle_name', label: 'Middle Name', type: 'text', group: 'identity', w: 'w-32' },
  { key: 'last_name', label: 'Last Name', type: 'text', group: 'identity', w: 'w-32' },
  { key: 'email', label: 'Email', type: 'text', group: 'identity', w: 'w-52' },
  { key: 'phone', label: 'Phone', type: 'text', group: 'identity', w: 'w-28' },
  { key: 'gender', label: 'Gender', type: 'select', options: GENDERS, group: 'identity', w: 'w-24' },
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date', group: 'identity', w: 'w-36' },
  { key: 'blood_group', label: 'Blood Group', type: 'select', options: BLOOD_GROUPS, group: 'identity', w: 'w-24' },
  { key: 'emergency_contact', label: 'Emergency Contact', type: 'text', group: 'identity', w: 'w-32' },
  { key: 'address', label: 'Address', type: 'text', group: 'identity', w: 'w-56' },
  { key: 'aadhaar_number', label: 'Aadhaar No.', type: 'text', group: 'identity', w: 'w-32' },
  { key: 'pan_number', label: 'PAN No.', type: 'text', group: 'identity', w: 'w-28' },

  // ---------- Job ----------
  { key: 'category', label: 'Category', type: 'combo', optionsFrom: 'categories', group: 'job', w: 'w-40' },
  { key: 'department', label: 'Department', type: 'combo', optionsFrom: 'departments', group: 'job', w: 'w-40' },
  { key: 'designation', label: 'Designation', type: 'combo', optionsFrom: 'designations', group: 'job', w: 'w-40' },
  { key: 'appointment_type', label: 'Appointment Type', type: 'combo', optionsFrom: 'appointment_types', group: 'job', w: 'w-32' },
  { key: 'date_of_joining', label: 'Date of Joining', type: 'date', group: 'job', w: 'w-36' },
  { key: 'pay_scale', label: 'Pay Scale', type: 'combo', optionsFrom: 'pay_scales', group: 'job', w: 'w-32' },
  { key: 'reports_to', label: 'Reports To', type: 'combo', optionsFrom: 'reports_to', group: 'job', w: 'w-36' },
  { key: 'display_order', label: 'Sequence', type: 'number', step: 1, group: 'job', w: 'w-20' },
  { key: 'is_active', label: 'Active', type: 'bool', group: 'job', w: 'w-16' },

  // ---------- Salary (editable inputs first, then live calculations) ----------
  { key: 'salary_type', label: 'Salary Type', type: 'select', options: SALARY_TYPES, group: 'salary', w: 'w-28', salary: true },
  { key: 'consolidated_salary', label: 'Consolidated Pay', type: 'number', group: 'salary', w: 'w-32', salary: true },
  { key: 'basic_salary', label: 'Basic', type: 'number', group: 'salary', w: 'w-28', salary: true },
  { key: 'agp', label: 'AGP', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'da_percentage', label: 'DA %', type: 'number', step: 0.01, group: 'salary', w: 'w-20', salary: true },
  { key: 'da', label: 'DA (fixed)', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'hra', label: 'HRA (fixed)', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'hod_allowance', label: 'HOD Allow.', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'special_allowance', label: 'Special Allow.', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'other_allowance', label: 'Other Allow.', type: 'number', group: 'salary', w: 'w-24', salary: true },
  { key: 'gross_basic', label: 'Gross Basic', calc: true, group: 'salary', w: 'w-24' },
  { key: 'da_amount', label: 'DA', calc: true, group: 'salary', w: 'w-24' },
  { key: 'hra_amount', label: 'HRA', calc: true, group: 'salary', w: 'w-24' },
  { key: 'gross_salary', label: 'GROSS', calc: true, group: 'salary', w: 'w-28', strong: true },
  { key: 'pf_amount', label: 'PF', calc: true, group: 'salary', w: 'w-20' },
  { key: 'pf_employer', label: 'PF (Employer)', calc: true, group: 'salary', w: 'w-24' },
  { key: 'profession_tax_amount', label: 'Prof. Tax', calc: true, group: 'salary', w: 'w-20' },
  { key: 'esic_amount', label: 'ESIC', calc: true, group: 'salary', w: 'w-20' },
  { key: 'total_deductions', label: 'Total Deductions', calc: true, group: 'salary', w: 'w-28' },
  { key: 'net_salary', label: 'NET SALARY', calc: true, group: 'salary', w: 'w-28', strong: true },

  // ---------- Statutory & flags ----------
  { key: 'pf_applicable', label: 'PF Applicable', type: 'bool', group: 'statutory', w: 'w-20', salary: true },
  { key: 'provident_fund', label: 'PF (fixed)', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'pt_applicable', label: 'PT Applicable', type: 'bool', group: 'statutory', w: 'w-20', salary: true },
  { key: 'profession_tax', label: 'Prof. Tax (fixed)', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'esic_applicable', label: 'ESIC Applicable', type: 'bool', group: 'statutory', w: 'w-20', salary: true },
  { key: 'esic', label: 'ESIC (fixed)', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'local_mart_applicable', label: 'Local Mart Appl.', type: 'bool', group: 'statutory', w: 'w-20', salary: true },
  { key: 'localmart', label: 'Local Mart', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'is_handicapped', label: 'Handicapped', type: 'bool', group: 'statutory', w: 'w-20' },
  { key: 'is_senior_citizen', label: 'Senior Citizen', type: 'bool', group: 'statutory', w: 'w-20' },
  { key: 'tds', label: 'TDS', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'bus', label: 'Bus', type: 'number', group: 'statutory', w: 'w-20', salary: true },
  { key: 'accommodation', label: 'Accommodation', type: 'number', group: 'statutory', w: 'w-24', salary: true },
  { key: 'other_deduction', label: 'Other Deduction', type: 'number', group: 'statutory', w: 'w-24', salary: true },

  // ---------- Bank ----------
  { key: 'bank_name', label: 'Bank Name', type: 'combo', optionsFrom: 'banks', group: 'bank', w: 'w-40' },
  { key: 'bank_account', label: 'Account No.', type: 'text', group: 'bank', w: 'w-40' },
  { key: 'ifsc_code', label: 'IFSC', type: 'text', group: 'bank', w: 'w-28' },

  // ---------- Qualification ----------
  { key: 'highest_qualification', label: 'Highest Qualification', type: 'combo', optionsFrom: 'qualifications', group: 'qualification', w: 'w-36' },
  { key: 'specialization', label: 'Specialization', type: 'combo', optionsFrom: 'specializations', group: 'qualification', w: 'w-40' },
  { key: 'experience_years', label: 'Experience (yrs)', type: 'number', step: 0.5, group: 'qualification', w: 'w-24' },
  { key: 'ugc_approval_status', label: 'UGC Approval', type: 'select', options: APPROVAL_STATUS, group: 'qualification', w: 'w-28' },
  { key: 'aicte_approval_status', label: 'AICTE Approval', type: 'select', options: APPROVAL_STATUS, group: 'qualification', w: 'w-28' },
  { key: 'api_score', label: 'API Score', type: 'number', step: 0.1, group: 'qualification', w: 'w-20' },

  // ---------- Attendance settings ----------
  { key: 'late_check_in_after', label: 'Late After', type: 'time', group: 'settings', w: 'w-28' },
  { key: 'morning_half_day_after', label: 'Half Day After', type: 'time', group: 'settings', w: 'w-28' },
  { key: 'face_match_tolerance', label: 'Face Tolerance', type: 'number', step: 0.01, group: 'settings', w: 'w-24' },
];

export const fieldsForGroup = (group) =>
  group === 'all' ? FIELDS : FIELDS.filter((f) => f.group === group);

export const EDITABLE_FIELDS = FIELDS.filter((f) => !f.calc);
export const COMBO_FIELDS = FIELDS.filter((f) => f.type === 'combo');
